import { useGlobalDataScopeOptions } from "./globalDataOptions";
import styles from "./GlobalDataTab.module.css";

const ScopeSelect = ({
  label,
  anyLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  anyLabel: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) => (
  <label className={styles.field}>
    <span className={styles.label}>{label}</span>
    <select
      className={styles.input}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{anyLabel}</option>
      {/* Keep the saved value selectable even if it's no longer in the live
          org list (org renamed / removed). */}
      {value && !options.includes(value) && <option value={value}>{value}</option>}
      {options.map((name) => (
        <option key={name} value={name}>
          {name}
        </option>
      ))}
    </select>
  </label>
);

export const ScopeFields = ({
  subject,
  tenantScope,
  landlordScope,
  onTenantScopeChange,
  onLandlordScopeChange,
  requireScope = false,
  compact = false,
}: {
  subject: string;
  tenantScope: string;
  landlordScope: string;
  onTenantScopeChange: (value: string) => void;
  onLandlordScopeChange: (value: string) => void;
  // A subject that must never reach every project (a Slack channel) needs one set.
  requireScope?: boolean;
  // Sized to sit among a settings card's small controls rather than in a form.
  compact?: boolean;
}) => {
  const { tenantOptions, landlordOptions, orgsError } = useGlobalDataScopeOptions();
  const optional = requireScope ? "" : " (optional)";
  const fields = (
    <>
      <div className={styles.scopeRow}>
        <ScopeSelect
          label={`Tenant scope${optional}`}
          anyLabel="Any tenant"
          value={tenantScope}
          options={tenantOptions}
          onChange={onTenantScopeChange}
        />
        <ScopeSelect
          label={`Landlord scope${optional}`}
          anyLabel="Any landlord"
          value={landlordScope}
          options={landlordOptions}
          onChange={onLandlordScopeChange}
        />
      </div>
      <div className={styles.scopeHint}>
        {requireScope
          ? `Set a tenant, a landlord or both: a ${subject} cannot apply portfolio-wide.`
          : `Scope narrows where a ${subject} applies: leave both as "Any" for portfolio-wide.`}{" "}
        When both are set they combine - the {subject} applies only to projects
        matching that tenant <em>and</em> that landlord.
        {orgsError && " (Couldn't load the org list - try reloading.)"}
      </div>
    </>
  );
  return compact ? <div className={styles.scopeFieldsCompact}>{fields}</div> : fields;
};
